import React, { useState, useEffect } from 'react';
import { signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { collection, doc, setDoc, onSnapshot, deleteDoc } from 'firebase/firestore';
import { auth, db, APP_ID_INTERNO } from './firebase';
import './App.css';

// Importaciones de nuestros futuros módulos
import LoginScreen from './components/LoginScreen';
import TournamentList from './components/TournamentList';
import PlayerManagement from './components/PlayerManagement';
import LeagueRanking from './components/LeagueRanking';
import TournamentManager from './phases/TournamentManager';

export default function App() {
    const [user, setUser] = useState(null);
    const [tournaments, setTournaments] = useState([]);
    const [activeTournamentId, setActiveTournamentId] = useState(null);
    const [role, setRole] = useState(null); // 'admin' o 'guest'
    const [showRanking, setShowRanking] = useState(false);
    const [showPlayers, setShowPlayers] = useState(false);

    // 1. Iniciar sesión anónima automáticamente
    useEffect(() => {
        const initAuth = async () => {
            try { 
                await signInAnonymously(auth); 
            } catch (e) { console.error(e); }
        };
        initAuth();
        const unsubscribe = onAuthStateChanged(auth, setUser);
        return () => unsubscribe();
    }, []);

    // 2. Cargar lista de torneos
    useEffect(() => {
        if (!user) return;
        const tournamentsRef = collection(db, 'torneos_beyblade', APP_ID_INTERNO, 'lista');
        const unsubscribe = onSnapshot(tournamentsRef, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            data.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
            setTournaments(data);
        });
        return () => unsubscribe();
    }, [user]);

    // 3. Funciones globales de Torneos
    const createTournament = async (tournamentData) => {
        const newId = crypto.randomUUID();
        const tournamentRef = doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'lista', newId);
        await setDoc(tournamentRef, {
            name: tournamentData.name,
            date: tournamentData.date || '',
            time: tournamentData.time || '',
            description: tournamentData.description || '',
            locationUrl: tournamentData.locationUrl || '',
            imageUrl: tournamentData.imageUrl || '',
            isUnranked: !!tournamentData.isUnranked,
            createdAt: Date.now(),
            phase: 'setup',
            players: [],
            phase1Matches: [],
            phase2Bracket: [],
        });
        setActiveTournamentId(newId);
    };

    const deleteTournament = async (id) => {
        if (!window.confirm("¿Seguro que quieres borrar este torneo?")) return;
        await deleteDoc(doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'lista', id));
        if (activeTournamentId === id) setActiveTournamentId(null);
    };

    const importTournament = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const importedData = JSON.parse(e.target.result);
                if (!importedData.name || !importedData.phase || !importedData.players) {
                    throw new Error("Formato de archivo inválido");
                }
                
                const tournamentId = importedData.id || crypto.randomUUID();
                const dataToSave = { ...importedData };
                delete dataToSave.id; 
                
                if (!window.confirm(`¿Quieres restaurar la copia de seguridad del torneo "${importedData.name}"?`)) {
                    event.target.value = null;
                    return;
                }

                await setDoc(doc(db, 'torneos_beyblade', APP_ID_INTERNO, 'lista', tournamentId), dataToSave);
                window.alert("Torneo restaurado con éxito.");
            } catch (error) {
                console.error(error);
                window.alert("Error al importar el torneo: Archivo corrupto o inválido.");
            }
            event.target.value = null; 
        };
        reader.readAsText(file);
    };

    // 4. Renderizado condicional
    if (!user) return <div className="loading-screen">Iniciando Beystadium...</div>;

    if (!role) return <LoginScreen setRole={setRole} />;

    const activeTournament = tournaments.find(t => t.id === activeTournamentId);

    return (
        <div className="app-container">
            <header className="app-header">
                <div className="header-content">
                    <div className="header-brand" onClick={() => { setActiveTournamentId(null); setShowRanking(false); setShowPlayers(false); }}>
                        <img src="/logoabl.png" alt="Logo Liga" className="app-logo" />
                        <h1 className="header-title">Liga Beyblade Alicante</h1>
                    </div>
                    <div className="header-actions">
                        {showPlayers && <div className="header-tag header-tag-highlight responsive-header-tag">Gestión de Jugadores</div>}
                        {showRanking && <div className="header-tag header-tag-warning responsive-header-tag">Ranking Global</div>}
                        {activeTournament && !showRanking && !showPlayers && <div className="header-tag responsive-header-tag">{activeTournament.name}</div>}
                        <button onClick={() => setRole(null)} className="btn btn-secondary" style={{padding: '0.25rem 0.75rem', fontSize: '0.7rem'}}>
                            Salir
                        </button>
                    </div>
                </div>
            </header>
            <main className="app-main">
                {showRanking ? (
                    <LeagueRanking tournaments={tournaments} onBack={() => setShowRanking(false)} role={role} />
                ) : showPlayers ? (
                    <PlayerManagement tournaments={tournaments} onBack={() => setShowPlayers(false)} role={role} />
                ) : !activeTournament ? (
                    <TournamentList 
                        tournaments={tournaments} 
                        onCreate={createTournament} 
                        onSelect={setActiveTournamentId} 
                        onDelete={deleteTournament} 
                        role={role} 
                        onShowRanking={() => setShowRanking(true)} 
                        onShowPlayers={() => setShowPlayers(true)}
                        onImport={importTournament}
                    />
                ) : (
                    <TournamentManager tournament={activeTournament} tournaments={tournaments} role={role} user={user} />
                )}
            </main>
        </div>
    );
}